// LAP-550 V1.2 — Page détail "Mon portefeuille" (répartition sectorielle + table positions)
import React, { useEffect, useState, useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import { useTranslation } from 'react-i18next'
import { makeGetRequest } from 'utils/api'
import DetailPageLayout from '../../_shared/DetailPageLayout'
import AssetTable from '../../_shared/AssetTable'
import { invTheme } from '../../_shared/theme'
import { textCol, numberCol, moneyCol, deltaCol } from '../../_shared/gridCols'

export default function PortfolioDetail() {
  const { t } = useTranslation('layout')
  const [positions, setPositions] = useState([])
  const [view, setView] = useState('sector')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const fetch = async () => {
      const r = await makeGetRequest('/api/investment/portfolio-investment-operations/?limit=200', {}, true).catch(() => null)
      if (r?.data?.results) setPositions(r.data.results)
    }
    fetch()
  }, [])

  // Agrégation par secteur / type pour le chart
  const agg = useMemo(() => {
    const key = view === 'sector' ? 'sector' : 'type'
    const map = new Map()
    positions.forEach((p) => {
      const k = p[key] || p.industry || '—'
      const v = parseFloat(p.current_value || 0) || 0
      map.set(k, (map.get(k) || 0) + v)
    })
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1])
  }, [positions, view])

  const chartData = {
    labels: agg.map(([k]) => k),
    datasets: [
      {
        label: t('investments.detail.totalSum', 'Total'),
        data: agg.map(([, v]) => v),
        backgroundColor: '#63a0ff',
        borderRadius: 4,
      },
    ],
  }
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: { datalabels: { display: false }, legend: { display: false } },
    scales: {
      x: { ticks: { color: 'rgba(255,255,255,0.55)' }, grid: { color: 'rgba(255,255,255,0.05)' } },
      y: { ticks: { color: 'rgba(255,255,255,0.7)' }, grid: { display: false } },
    },
  }

  const totalValue = positions.reduce((s, p) => s + (parseFloat(p.current_value) || 0), 0)

  // Les colonnes chiffrées passent par les fabriques (_shared/gridCols) : la
  // valeur brute sert au tri et aux filtres, le formatage reste à l'affichage.
  const columns = useMemo(
    () => [
      textCol({ field: 'type', headerName: t('investments.portfolio.cols.type'), width: 70 }),
      textCol({
        field: 'name',
        headerName: t('investments.portfolio.cols.title'),
        width: 160,
        renderCell: (p) => <span style={{ fontWeight: 600 }}>{p.row.name}</span>,
      }),
      textCol({ field: 'sector', headerName: t('investments.detail.cols.sector', 'Secteur'), width: 130 }),
      numberCol({ field: 'quantity', headerName: t('investments.portfolio.cols.quantity'), width: 90, decimals: 2 }),
      moneyCol({ field: 'average_price', headerName: t('investments.portfolio.cols.avgPrice'), width: 110 }),
      moneyCol({ field: 'current_price', headerName: t('investments.portfolio.cols.currentPrice'), width: 110 }),
      moneyCol({ field: 'current_value', headerName: t('investments.portfolio.cols.value'), width: 120 }),
      deltaCol({ field: 'value_change', headerName: t('investments.portfolio.cols.valueChange'), width: 120, unit: '' }),
      deltaCol({ field: 'profitability', headerName: t('investments.portfolio.cols.profitability'), width: 130 }),
    ],
    [t],
  )

  return (
    <DetailPageLayout
      title={t('investments.portfolio.title')}
      subtitle={t('investments.detail.portfolioSubtitle', 'Répartition sectorielle de mon portefeuille')}
      icon={<AccountTreeOutlinedIcon fontSize="small" />}
      accent={invTheme.accents.portfolio}
      chartSubtitleOptions={[
        { value: 'sector', label: t('investments.detail.bySector', 'Par secteur') },
        { value: 'type', label: t('investments.detail.byType', 'Par type') },
      ]}
      chartSubtitle={view}
      onChartSubtitleChange={setView}
      sumLabel={t('investments.detail.totalSum', 'Total')}
      sumValue={totalValue ? `${totalValue.toFixed(0)} €` : '—'}
      chart={<Bar data={chartData} options={chartOptions} />}
      tableTitle={t('investments.detail.portfolioTableTitle', 'Mon portefeuille')}
      tableCount={positions.length}
      tableContent={
        <AssetTable
          rows={positions}
          columns={columns}
          emptyLabel={t('investments.portfolio.noRows')}
          searchable
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('investments.detail.searchPositions', 'Rechercher une position, un secteur…')}
        />
      }
    />
  )
}
